import React, { useState, useEffect, useRef } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  message, 
  Popconfirm, 
  Card, 
  Typography, 
  Pagination,
  Row,
  Col
} from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, SearchOutlined } from '@ant-design/icons';
import { 
  getEzvizVideoTokens, 
  createEzvizVideoToken, 
  deleteEzvizVideoToken, 
  updateEzvizVideoToken,
  EzvizVideoToken 
} from '../../services/api';

const { Title } = Typography;

// Debounce function to limit API calls
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  
  return debouncedValue;
};

const EzvizVideoManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [deletingIds, setDeletingIds] = useState<string[]>([]);
  const [videoTokens, setVideoTokens] = useState<EzvizVideoToken[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingToken, setEditingToken] = useState<EzvizVideoToken | null>(null);
  const [form] = Form.useForm();
  const [search, setSearch] = useState<string>('');
  const debouncedSearch = useDebounce(search, 500); // 500ms debounce

  // Fetch video tokens on component mount and when pagination or search changes
  useEffect(() => {
    // Create a single function to fetch data that includes all necessary parameters
    const fetchData = async () => {
      await fetchVideoTokens();
    };
    
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, debouncedSearch]);

  // Fetch video tokens from the API
  const fetchVideoTokens = async () => {
    setLoading(true);
    try {
      const { data } = await getEzvizVideoTokens(pagination.current, pagination.pageSize, debouncedSearch);
      console.log('API response:', data); // Add logging to debug
      
      if (data.code === 0 && data.data) {
        // Clear the existing tokens first before setting new ones
        setVideoTokens(data.data.items || []);
        setPagination({
          ...pagination,
          total: data.data.pagination.total,
          totalPages: data.data.pagination.totalPages
        });
      } else {
        setVideoTokens([]); // Clear tokens on error
        message.error(data.msg || '获取萤石云视频账户失败');
      }
    } catch (error) {
      console.error('Error fetching video tokens:', error);
      setVideoTokens([]); // Clear tokens on error
      message.error('获取萤石云视频账户失败');
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    // Update both search and pagination in a single operation
    setSearch(searchValue);
    setPagination({
      ...pagination,
      current: 1
    });
  };

  // Handle adding a new video token
  const handleAddToken = () => {
    setIsEditing(false);
    setEditingToken(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  // Handle editing a video token
  const handleEdit = (record: EzvizVideoToken) => {
    setIsEditing(true);
    setEditingToken(record);
    form.setFieldsValue({
      appKey: record.appKey,
      appSecret: record.appSecret,
      remark: record.remark
    });
    setIsModalVisible(true);
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      
      if (isEditing && editingToken) {
        // Update existing token
        const { data } = await updateEzvizVideoToken(editingToken.uid, {
          appKey: values.appKey,
          appSecret: values.appSecret,
          remark: values.remark
        });
        
        if (data.code === 0) {
          message.success('更新萤石云视频账户成功');
          setIsModalVisible(false);
          form.resetFields();
          fetchVideoTokens();
        } else {
          message.error(data.msg || '更新萤石云视频账户失败');
        }
      } else {
        // Create new token
        const { data } = await createEzvizVideoToken({
          appKey: values.appKey,
          appSecret: values.appSecret,
          remark: values.remark
        });
        
        if (data.code === 0) {
          message.success('添加萤石云视频账户成功');
          setIsModalVisible(false);
          form.resetFields();
          fetchVideoTokens();
        } else {
          message.error(data.msg || '添加萤石云视频账户失败');
        }
      }
    } catch (error) {
      console.error('Error submitting video token:', error);
      message.error(isEditing ? '更新萤石云视频账户失败' : '添加萤石云视频账户失败');
    } finally {
      setLoading(false);
    }
  };

  // Handle deleting a video token
  const handleDelete = async (uid: string) => {
    setDeletingIds(prev => [...prev, uid]);
    try {
      const { data } = await deleteEzvizVideoToken(uid);
      if (data.code === 0) {
        message.success('删除萤石云视频账户成功');
        fetchVideoTokens();
      } else {
        message.error(data.msg || '删除萤石云视频账户失败');
      }
    } catch (error) {
      console.error('Error deleting video token:', error);
      message.error('删除萤石云视频账户失败');
    } finally {
      setDeletingIds(prev => prev.filter(id => id !== uid));
    }
  };

  // Handle pagination change
  const handlePageChange = (page: number, pageSize?: number) => {
    setPagination({
      ...pagination,
      current: page,
      pageSize: pageSize || pagination.pageSize
    });
    // No need to call fetchVideoTokens here as it will be triggered by the useEffect
  };

  // Table columns
  const columns = [
    {
      title: 'UID',
      dataIndex: 'uid',
      key: 'uid',
      width: 80
    },
    {
      title: 'AppKey',
      dataIndex: 'appKey',
      key: 'appKey',
      width: 250
    },
    {
      title: 'AppSecret',
      dataIndex: 'appSecret',
      key: 'appSecret',
      width: 250,
      render: (text: string) => (
        <span>{text.substring(0, 8)}...{text.substring(text.length - 8)}</span>
      )
    },
    {
      title: '过期时间',
      dataIndex: 'tokenExpire',
      key: 'tokenExpire',
      width: 180,
      render: (timestamp: number) => new Date(timestamp).toLocaleString()
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 150
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_: any, record: EzvizVideoToken) => (
        <Space size="middle">
          <Button 
            type="text" 
            icon={<EditOutlined />} 
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="确定要删除此萤石云视频账户吗?"
            onConfirm={() => handleDelete(record.uid)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              type="text" 
              danger 
              icon={<DeleteOutlined />}
              loading={deletingIds.includes(record.uid)}
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
          <Title level={4}>萤石云视频账户管理</Title>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAddToken}
          >
            添加账户
          </Button>
        </div>
        
        <Row gutter={16} style={{ marginBottom: '16px' }}>
          <Col span={8}>
            <Input
              placeholder="搜索 AppKey"
              prefix={<SearchOutlined />}
              value={search}
              onChange={handleSearchChange}
              allowClear
            />
          </Col>
        </Row>
        
        <Table 
          columns={columns} 
          dataSource={videoTokens} 
          rowKey="uid"
          loading={loading}
          pagination={false}
          scroll={{ x: 1200 }}
          locale={{ emptyText: '暂无数据' }}
        />
        
        <div style={{ marginTop: '16px', textAlign: 'right' }}>
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={handlePageChange}
            showSizeChanger={false}
            showTotal={(total) => `共 ${total} 条记录`}
          />
        </div>
      </Card>

      {/* Add/Edit Token Modal */}
      <Modal
        title={isEditing ? "编辑萤石云视频账户" : "添加萤石云视频账户"}
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        confirmLoading={loading}
        maskClosable={false}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="appKey"
            label="AppKey"
            rules={[{ required: true, message: '请输入AppKey' }]}
          >
            <Input placeholder="请输入AppKey" />
          </Form.Item>
          
          <Form.Item
            name="appSecret"
            label="AppSecret"
            rules={[{ required: true, message: '请输入AppSecret' }]}
          >
            <Input placeholder="请输入AppSecret" />
          </Form.Item>
          
          <Form.Item
            name="remark"
            label="备注"
            rules={[{ required: true, message: '请输入备注' }]}
          >
            <Input placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EzvizVideoManagement; 